import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { DischargeChecklistItem } from 'models/DischargeChecklistItem';
import { destroyDischargeChecklistItem, dischargeChecklistQueryKeys } from 'services/api/dischargeChecklists';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

type Props = {
  item: DischargeChecklistItem;
  clientId?: string;
  onClose: () => void;
};

export default function DeleteDischargeChecklistItemModal({ item, clientId, onClose }: Props) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  // onSuccess on the hook, not per call: the header's close button stays live while the delete is
  // in flight, and React Query drops per-call callbacks when the modal unmounts.
  const { mutate, isPending } = useMutateWithToast({
    mutationFn: destroyDischargeChecklistItem,
    errorToast: validationErrorMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dischargeChecklistQueryKeys.base });
      addToast({ text: 'Checklist item deleted' });
      onClose();
    },
  });

  return (
    <StandardModal title='Delete Checklist Item?' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <StandardModal.RecordName>{item.label}</StandardModal.RecordName>
      <Content>
        <strong>Are you sure you wish to delete this checklist item?</strong> It will no longer be displayed in the
        Discharge Planning section of the patient story moving forward for new patients.
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant='danger'
          onClick={() => mutate({ id: item.id, clientId })}
          loading={isPending}
          disabled={isPending}
          data-testid='confirm-delete-checklist-item'>
          Delete
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
